<template>
  <!--
    Contenedor global de toasts (store `notification`). Se monta UNA vez en App.vue.

    - `popover="manual"`: pasa a la capa superior del navegador (top layer) para no quedar
      oculto detrás del backdrop de un <dialog>.showModal() (IspModal). Si el navegador no
      soporta Popover API, queda como `fixed` + z-[9999] (igual que los toasts locales).
    - `pointer-events-none` en la raíz: el hueco entre toasts no bloquea la página.
    - Ancho: min(24rem, 100vw - 2rem) -> 320px: 288px; 380px: 348px; 1280px: 384px.
  -->
  <div
    ref="rootRef"
    data-testid="toast-container"
    role="region"
    aria-label="Notificaciones"
    popover="manual"
    class="toast toast-end toast-bottom z-[9999] m-0 p-0 border-0 overflow-visible pointer-events-none w-[min(24rem,calc(100vw-2rem))] bottom-[max(1rem,env(safe-area-inset-bottom))]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @focusin="focused = true"
    @focusout="onFocusOut"
  >
    <div
      v-for="toast in visibleToasts"
      :key="toast.key"
      data-testid="toast"
      :data-type="toast.type"
      class="alert pointer-events-auto items-start gap-3 shadow-lg"
      :class="ALERT_CLASS[toast.type]"
      :role="toast.type === 'error' ? 'alert' : 'status'"
      :aria-live="toast.type === 'error' ? 'assertive' : 'polite'"
      aria-atomic="true"
    >
      <component :is="ICON[toast.type]" class="h-5 w-5 shrink-0" aria-hidden="true" />

      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug break-words">
          {{ toast.title }}
          <span
            v-if="toast.count > 1"
            data-testid="toast-count"
            class="badge badge-neutral badge-sm ml-1 align-middle"
          >
            <span aria-hidden="true">×{{ toast.count }}</span>
            <span class="sr-only"> (repetida {{ toast.count }} veces)</span>
          </span>
        </p>
        <p v-if="toast.message" class="text-sm leading-snug break-words">{{ toast.message }}</p>
      </div>

      <!-- after:-inset-1.5 amplía el área táctil de 32px a 44px sin agrandar el botón -->
      <button
        type="button"
        data-testid="toast-dismiss"
        class="btn btn-ghost btn-circle btn-sm relative after:absolute after:-inset-1.5 after:content-['']"
        aria-label="Cerrar notificación"
        @click="dismiss(toast)"
      >
        <XMarkIcon class="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { useNotificationStore, type NotificationType } from '@/stores/notification'

/** Toasts visibles a la vez; los más antiguos salen de la vista (siguen en el store hasta expirar). */
const MAX_VISIBLE = 4

const ALERT_CLASS: Record<NotificationType, string> = {
  error: 'alert-error',
  warning: 'alert-warning',
  success: 'alert-success',
  info: 'alert-info',
}

const ICON = {
  error: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  info: InformationCircleIcon,
}

interface ToastView {
  /** id de la última notificación del grupo: un duplicado nuevo remonta el toast (re-anuncia y re-anima). */
  key: string
  ids: string[]
  type: NotificationType
  title: string
  message: string
  count: number
}

const store = useNotificationStore()
const rootRef = ref<HTMLElement | null>(null)

// ---- Vista: colapsa duplicados CONSECUTIVOS idénticos (tipo + título + mensaje) ----
const toasts = computed<ToastView[]>(() => {
  const groups: ToastView[] = []
  for (const n of store.notifications) {
    const title = String(n.title ?? '')
    const message = String(n.message ?? '')
    const last = groups[groups.length - 1]
    if (last && last.type === n.type && last.title === title && last.message === message) {
      last.ids.push(n.id)
      last.count += 1
      last.key = n.id
    } else {
      groups.push({ key: n.id, ids: [n.id], type: n.type, title, message, count: 1 })
    }
  }
  return groups
})

const visibleToasts = computed(() => toasts.value.slice(-MAX_VISIBLE))

const dismiss = (toast: ToastView) => {
  for (const id of toast.ids) store.remove(id)
}

// ---- Pausa por hover / foco (a nivel de contenedor: sobrevive al remontaje de un toast) ----
const hovered = ref(false)
const focused = ref(false)
const paused = computed(() => hovered.value || focused.value)

const onFocusOut = (event: FocusEvent) => {
  const next = event.relatedTarget as Node | null
  if (!next || !rootRef.value?.contains(next)) focused.value = false
}

// Aplica el estado a TODAS las notificaciones vivas (incluidas las que llegan mientras se pausa).
// pause()/resume() del store son idempotentes. Flush por defecto (NO sync): el store hace push
// y DESPUÉS arranca el countdown, así que hay que esperar a que `add` termine.
watch(
  [paused, () => store.notifications.map((n) => n.id).join('|')],
  () => {
    for (const { id } of store.notifications) {
      if (paused.value) store.pause(id)
      else store.resume(id)
    }
  },
)

// Un toast quitado bajo el puntero no emite mouseleave/blur: re-sincroniza cuando cambia la pila.
watch(
  () => toasts.value.map((t) => t.key).join('|'),
  () => {
    if (store.notifications.length === 0) hovered.value = false
    focused.value = !!rootRef.value && rootRef.value.contains(document.activeElement)
  },
  { flush: 'post' },
)

// ---- Capa superior (Popover API) ----
const raise = () => {
  const el = rootRef.value as (HTMLElement & { showPopover?: () => void; hidePopover?: () => void }) | null
  if (!el || typeof el.showPopover !== 'function') return
  try { el.hidePopover?.() } catch { /* no estaba abierto */ }
  try { el.showPopover() } catch { /* sin conexión al documento / ya abierto */ }
}

onMounted(raise)

// Un <dialog> modal abierto DESPUÉS queda por encima: cada toast nuevo vuelve a subir el contenedor.
watch(
  () => store.notifications[store.notifications.length - 1]?.id,
  (id) => { if (id) raise() },
  { flush: 'post' },
)

onBeforeUnmount(() => {
  // Si se desmonta con el puntero encima, no dejar countdowns congelados.
  hovered.value = false
  focused.value = false
  for (const { id } of store.notifications) store.resume(id)
})
</script>
